import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, ChevronDown, ChevronUp, Upload, Loader2, BookOpen, PenLine, X, Save,
  FileText, Brain, Globe, Building2, Copy, History,
} from 'lucide-react';
import { KBSubtypesEditor } from '../../../pages/admin/KBSubtypesEditor';
import { API, extractErr } from '../utils';
import { CriterionRow } from './CriterionRow';
import { KBSparkline } from './KBSparkline';
import { KBHistoryPanel } from './KBHistoryPanel';

const EMPTY_CRITERION = { description: '', weight: 1 };

/**
 * Admin-facing KB row with inline edit, reassign, clone, sparkline, history
 * and regrade actions. Rendered once per KB inside the AdminKnowledgeBasesPage.
 */
export const KBCard = ({ kb, token, onUpdated, onQuiz, selectedDomainId, domains = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [sparkline, setSparkline] = useState(null);
  const [sparkLoading, setSparkLoading] = useState(false);
  const [draftingInstructions, setDraftingInstructions] = useState(false);
  const [regrading, setRegrading] = useState(false);
  const [regradeDays, setRegradeDays] = useState(7);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const isDomainKB = kb.domain_id && kb.domain_id !== null;

  const startEdit = () => {
    setForm({
      name: kb.name,
      call_type: kb.call_type,
      description: kb.description,
      threshold_score: kb.threshold_score,
      grading_instructions: kb.grading_instructions || '',
      escalation_emails: [...(kb.escalation_emails || [])],
      governance_rules: [...(kb.governance_rules || [])],
      criteria: (kb.criteria || []).map(c => ({ description: c.description, weight: c.weight || 1 })),
      subtypes: (kb.subtypes || []).map(s => ({ ...s })),
    });
    setEditing(true);
    setExpanded(true);
  };

  const addCriterion = () => setForm(f => ({ ...f, criteria: [...f.criteria, { ...EMPTY_CRITERION }] }));
  const updateCriterion = (i, k, v) => setForm(f => {
    const c = [...f.criteria]; c[i] = { ...c[i], [k]: v }; return { ...f, criteria: c };
  });
  const removeCriterion = i => setForm(f => ({ ...f, criteria: f.criteria.filter((_, idx) => idx !== i) }));

  const saveEdit = async ({ keepOpen = false } = {}) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/knowledge-bases/${kb.id}`, form, { headers });
      toast.success('Knowledge base updated');
      // Auto-regrade fires when governance_rules were added or edited.
      // Server returns `auto_regrade: {queued, days, ...}` — surface it as
      // a second toast so the manager knows the compliance digest will
      // catch up on its own.
      const ar = r?.data?.auto_regrade;
      if (ar?.queued) {
        toast.info(
          `Auto-regrading ${ar.queued} recent call${ar.queued === 1 ? '' : 's'} against the new rule${ar.rules_added === 1 ? '' : 's'} · refresh in a few minutes`,
          { duration: 6000 },
        );
      }
      if (!keepOpen) setEditing(false);
      onUpdated();
      return true;
    } catch (err) {
      console.error('KB save error', err?.response || err);
      toast.error(extractErr(err, 'Failed to save'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      // IMPORTANT: do NOT set Content-Type manually for FormData — axios/browser
      // must set it to "multipart/form-data; boundary=..." with the proper
      // auto-generated boundary, otherwise FastAPI can't parse the body.
      await axios.post(`${API}/knowledge-bases/${kb.id}/upload-doc`, fd, { headers });
      toast.success(`${file.name} uploaded`);
      onUpdated();
    } catch (err) {
      console.error('KB upload error', err?.response || err);
      toast.error(extractErr(err, 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await axios.delete(`${API}/knowledge-bases/${kb.id}/documents/${encodeURIComponent(filename)}`, { headers });
      toast.success(`${filename} deleted`);
      onUpdated();
    } catch (err) {
      console.error('KB delete error', err?.response || err);
      toast.error(extractErr(err, 'Delete failed'));
    }
  };

  const handleDownload = async (filename) => {
    const url = `${API}/knowledge-bases/${kb.id}/documents/${encodeURIComponent(filename)}`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        let msg = `Download failed (HTTP ${r.status})`;
        try {
          const body = await r.json();
          if (body?.detail) msg = typeof body.detail === 'string' ? body.detail : msg;
        } catch (e) {
          console.debug('KB download: error body was not JSON, using status-text', e?.message);
        }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('KB download error', err);
      toast.error(err?.message || 'Download failed');
    }
  };

  const handlePreviewRegrade = async () => {
    if (previewing) return;
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const r = await axios.post(`${API}/knowledge-bases/${kb.id}/regrade-preview?days=${regradeDays}&samples=5`, {}, { headers, timeout: 180000 });
      setPreviewResult(r.data);
      if (!r.data?.samples?.length) {
        toast.info(r.data?.message || 'No calls available to preview.');
      }
    } catch (err) {
      console.error('KB regrade-preview error', err?.response || err);
      toast.error(extractErr(err, 'Preview failed'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleRegrade = async () => {
    if (regrading) return;
    if (!window.confirm(`Re-grade all "${kb.name}" calls from the last ${regradeDays} day(s) using the currently-saved instructions? Existing scores will be reset.`)) return;
    setRegrading(true);
    try {
      const r = await axios.post(`${API}/knowledge-bases/${kb.id}/regrade?days=${regradeDays}`, {}, { headers });
      toast.success(r.data?.message || `Regrading ${r.data?.count || 0} calls…`);
      onUpdated();
    } catch (err) {
      console.error('KB regrade error', err?.response || err);
      toast.error(extractErr(err, 'Regrade failed'));
    } finally {
      setRegrading(false);
    }
  };

  const handleReassign = async (newDomainId) => {
    setReassigning(true);
    try {
      await axios.patch(`${API}/knowledge-bases/${kb.id}/domain`, { domain_id: newDomainId }, { headers });
      const label = newDomainId
        ? (domains.find(d => d.id === newDomainId)?.company?.name || domains.find(d => d.id === newDomainId)?.yabbit_domain || 'domain')
        : 'Global templates';
      toast.success(`Moved "${kb.name}" → ${label}`);
      setReassignOpen(false);
      onUpdated();
    } catch (err) {
      console.error('KB reassign error', err?.response || err);
      toast.error(extractErr(err, 'Reassign failed'));
    } finally {
      setReassigning(false);
    }
  };

  const handleClone = async (targetDomainId) => {
    setCloning(true);
    try {
      await axios.post(`${API}/knowledge-bases/${kb.id}/clone`, { domain_id: targetDomainId }, { headers });
      const label = targetDomainId
        ? (domains.find(d => d.id === targetDomainId)?.company?.name || domains.find(d => d.id === targetDomainId)?.yabbit_domain || 'domain')
        : 'Global templates';
      toast.success(`Cloned "${kb.name}" → ${label}`);
      setCloneOpen(false);
      onUpdated();
    } catch (err) {
      console.error('KB clone error', err?.response || err);
      toast.error(extractErr(err, 'Clone failed'));
    } finally {
      setCloning(false);
    }
  };

  // Load sparkline lazily — only when the card is expanded, and cache on
  // the component (fetched once per open unless the parent forces remount).
  useEffect(() => {
    if (!expanded || sparkline || sparkLoading) return;
    let cancelled = false;
    (async () => {
      setSparkLoading(true);
      try {
        const r = await axios.get(`${API}/knowledge-bases/${kb.id}/sparkline?days=30`, { headers });
        if (!cancelled) setSparkline(r.data?.days || []);
      } catch (err) {
        console.debug('KB sparkline fetch failed', err?.message);
        if (!cancelled) setSparkline([]);
      } finally {
        if (!cancelled) setSparkLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // headers changes each render but token is stable; guard by kb.id only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, kb.id]);

  const scoreColor = kb.threshold_score >= 80 ? 'text-emerald-400' : kb.threshold_score >= 60 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-white/[0.03] transition-colors"
        onClick={() => { if (!editing) setExpanded(e => !e); }}
      >
        <BookOpen className="w-4 h-4 text-teal-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white font-medium text-sm">{kb.name}</p>
            {isDomainKB
              ? <span className="text-[10px] px-1.5 py-0.5 bg-teal-500/15 text-teal-400 border border-teal-500/20 rounded-full">Domain</span>
              : <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 text-amber-400/70 border border-amber-500/20 rounded-full flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" />Global</span>
            }
          </div>
          <p className="text-white/30 text-xs">{kb.call_type.replace(/_/g, ' ')} · {kb.criteria?.length || 0} criteria</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs font-semibold ${scoreColor}`}>Threshold {kb.threshold_score}%</span>
          <span className="text-xs text-white/20">{kb.documents?.length || 0} docs</span>
          {selectedDomainId && (
            <button
              onClick={e => { e.stopPropagation(); onQuiz(kb); }}
              className="flex items-center gap-1 text-xs px-2 py-1 bg-purple-500/15 hover:bg-purple-500/25 text-purple-300 border border-purple-500/20 rounded-lg transition-colors"
              data-testid={`quiz-kb-${kb.id}`}
              title="Build with AI Coach"
            >
              <Brain className="w-3 h-3" />AI Coach
            </button>
          )}
          <button
            onClick={e => { e.stopPropagation(); startEdit(); }}
            className="text-white/30 hover:text-teal-400 transition-colors"
            data-testid={`edit-kb-${kb.id}`}
          >
            <PenLine className="w-4 h-4" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); setHistoryOpen(o => !o); }}
            className={`transition-colors ${historyOpen ? 'text-teal-400' : 'text-white/30 hover:text-teal-400'}`}
            data-testid={`history-kb-${kb.id}`}
            title="Change history — every edit, AI draft and regrade run"
          >
            <History className="w-4 h-4" />
          </button>
          {domains.length > 0 && (
            <div className="relative">
              <button
                onClick={e => { e.stopPropagation(); setReassignOpen(o => !o); setCloneOpen(false); }}
                className="text-white/30 hover:text-teal-400 transition-colors flex items-center"
                data-testid={`reassign-kb-${kb.id}`}
                title="Reassign to a different domain"
              >
                <Building2 className="w-4 h-4" />
              </button>
              {reassignOpen && (
                <div
                  onClick={e => e.stopPropagation()}
                  className="absolute right-0 top-6 z-30 w-64 bg-slate-900 border border-white/10 rounded-lg shadow-xl p-2 space-y-1"
                  data-testid={`reassign-panel-${kb.id}`}
                >
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-white/40">Move this KB to…</div>
                  <button
                    disabled={reassigning || !kb.domain_id}
                    onClick={() => handleReassign(null)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${!kb.domain_id ? 'bg-amber-500/10 text-amber-300' : 'hover:bg-white/5 text-white/70'}`}
                    data-testid={`reassign-to-global-${kb.id}`}
                  >
                    <Globe className="w-3 h-3" />Global templates
                    {!kb.domain_id && <span className="ml-auto text-[10px] text-white/30">current</span>}
                  </button>
                  <div className="border-t border-white/5 my-1"></div>
                  <div className="max-h-56 overflow-y-auto">
                    {domains.map(d => {
                      const isCurrent = d.id === kb.domain_id;
                      return (
                        <button
                          key={d.id}
                          disabled={reassigning || isCurrent}
                          onClick={() => handleReassign(d.id)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${isCurrent ? 'bg-teal-500/10 text-teal-300' : 'hover:bg-white/5 text-white/70'}`}
                          data-testid={`reassign-to-${d.id}-${kb.id}`}
                        >
                          <Building2 className="w-3 h-3" />
                          <span className="truncate">{d.company?.name || d.yabbit_domain}</span>
                          {isCurrent && <span className="ml-auto text-[10px] text-white/30">current</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {domains.length > 0 && (
            <div className="relative">
              <button
                onClick={e => { e.stopPropagation(); setCloneOpen(o => !o); setReassignOpen(false); }}
                className="text-white/30 hover:text-cyan-300 transition-colors flex items-center"
                data-testid={`clone-kb-${kb.id}`}
                title="Clone into another domain"
              >
                <Copy className="w-4 h-4" />
              </button>
              {cloneOpen && (
                <div
                  onClick={e => e.stopPropagation()}
                  className="absolute right-0 top-6 z-30 w-64 bg-slate-900 border border-white/10 rounded-lg shadow-xl p-2 space-y-1"
                  data-testid={`clone-panel-${kb.id}`}
                >
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-white/40">Clone a copy into…</div>
                  <button
                    disabled={cloning}
                    onClick={() => handleClone(null)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors hover:bg-white/5 text-white/70"
                    data-testid={`clone-to-global-${kb.id}`}
                  >
                    <Globe className="w-3 h-3" />Global templates
                  </button>
                  <div className="border-t border-white/5 my-1"></div>
                  <div className="max-h-56 overflow-y-auto">
                    {domains.map(d => (
                      <button
                        key={d.id}
                        disabled={cloning}
                        onClick={() => handleClone(d.id)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors hover:bg-white/5 text-white/70"
                        data-testid={`clone-to-${d.id}-${kb.id}`}
                      >
                        <Building2 className="w-3 h-3" />
                        <span className="truncate">{d.company?.name || d.yabbit_domain}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-white/20" /> : <ChevronDown className="w-4 h-4 text-white/20" />}
        </div>
      </div>

      {historyOpen && <div className="px-5 pb-2"><KBHistoryPanel kbId={kb.id} token={token} onClose={() => setHistoryOpen(false)} /></div>}

      {expanded && (
        <div className="border-t border-white/5 px-5 py-4 space-y-4">
          <KBSparkline data={sparkline} loading={sparkLoading} threshold={kb.threshold_score} />
          {editing && form ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/40 block mb-1">Name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50" />
                </div>
                <div>
                  <label className="text-xs text-white/40 block mb-1">Call / Coaching Type</label>
                  <input value={form.call_type} onChange={e => setForm(f => ({ ...f, call_type: e.target.value }))}
                    placeholder="e.g. sales_new, it_technical"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-white/40 block mb-1">Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-teal-500/50" />
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-white/40 flex items-center gap-2">
                      Grading Instructions
                      <span className="text-[10px] text-amber-300/70 uppercase tracking-wider">Primary authority</span>
                    </label>
                    <button
                      type="button"
                      onClick={async () => {
                        if (draftingInstructions) return;
                        setDraftingInstructions(true);
                        try {
                          const r = await axios.post(`${API}/knowledge-bases/${kb.id}/draft-instructions`, {}, { headers });
                          setForm(f => ({ ...f, grading_instructions: r.data?.grading_instructions || '' }));
                          const doc_chars = r.data?.doc_chars || 0;
                          toast.success(
                            doc_chars > 0
                              ? `Draft generated from ${doc_chars.toLocaleString()} chars of uploaded docs. Review and edit before saving.`
                              : 'Draft generated (no docs uploaded — used call type + criteria). Review before saving.',
                          );
                        } catch (err) {
                          console.error('AI draft error', err?.response || err);
                          toast.error(extractErr(err, 'AI draft failed'));
                        } finally {
                          setDraftingInstructions(false);
                        }
                      }}
                      disabled={draftingInstructions}
                      className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-200 transition-colors disabled:opacity-50"
                      data-testid="kb-ai-draft-instructions-btn"
                      title="Read uploaded documents + criteria and auto-generate a first-draft coaching prompt"
                    >
                      {draftingInstructions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                      {draftingInstructions ? 'Drafting…' : 'AI Draft'}
                    </button>
                  </div>
                  <textarea
                    value={form.grading_instructions || ''}
                    onChange={e => setForm(f => ({ ...f, grading_instructions: e.target.value }))}
                    rows={6}
                    maxLength={4000}
                    placeholder={"Free-text prompt that steers grading — read by the AI FIRST, above every criterion and reference document.\n\nExample:\n- For \"New Member Sales\" calls, the agent MUST introduce themselves by name and ask for the caller\u2019s goal within the first 60 seconds.\n- Disqualify (score < 30) any call where the agent uses the phrase \"we can't help you\" without offering an alternative.\n- Bonus 10 points if the agent books a tour before the call ends."}
                    className="w-full bg-white/5 border border-amber-500/20 rounded-lg px-3 py-2 text-white text-sm resize-y focus:outline-none focus:border-amber-500/50 leading-relaxed"
                    data-testid="kb-grading-instructions"
                  />
                  <p className="text-white/30 text-[10px] mt-1">
                    {(form.grading_instructions || '').length}/4000 · overrides preset criteria and uploaded documents on any conflict
                  </p>
                </div>
                <div>
                  <label className="text-xs text-white/40 block mb-1">Escalation Threshold (%)</label>
                  <input type="number" min={0} max={100} value={form.threshold_score}
                    onChange={e => setForm(f => ({ ...f, threshold_score: parseInt(e.target.value) || 70 }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50" />
                </div>
                <div>
                  <label className="text-xs text-white/40 block mb-1">Escalation Emails (comma-sep)</label>
                  <input value={(form.escalation_emails || []).join(', ')}
                    onChange={e => setForm(f => ({ ...f, escalation_emails: e.target.value.split(',').map(x => x.trim()).filter(Boolean) }))}
                    placeholder="manager@company.com, ..."
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50" />
                </div>
              </div>

              {/* Governance rules — one rule per line. Any rule the LLM
                  detects a violation of surfaces in the Grading Sidebar's
                  Governance Violations card (independent of the criteria
                  checklist). Empty = no compliance triggers for this KB. */}
              <div>
                <label className="text-xs text-white/40 block mb-1">
                  Governance / Disqualifying Rules ({(form.governance_rules || []).length})
                </label>
                <textarea
                  value={(form.governance_rules || []).join('\n')}
                  onChange={e => setForm(f => ({
                    ...f,
                    governance_rules: e.target.value.split('\n').map(x => x.trim()).filter(Boolean),
                  }))}
                  rows={5}
                  placeholder={"One rule per line. e.g.:\nRude or dismissive language toward the prospect\nPromising outcomes that can't be guaranteed\nMisrepresenting pricing or cancellation terms"}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50 resize-none font-mono"
                  data-testid={`kb-governance-rules-${kb.id}`}
                />
                <p className="text-[10px] text-white/30 mt-1">
                  Rules render as red flags on graded calls. Independent of the criteria checklist — a violation is a hard fail regardless of overall score.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white/60 text-xs font-medium uppercase tracking-wide">Grading Criteria ({form.criteria.length})</p>
                  <button onClick={addCriterion} className="flex items-center gap-1 text-teal-400 text-xs hover:text-teal-300"><Plus className="w-3.5 h-3.5" />Add</button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {form.criteria.map((c, i) => (
                    <CriterionRow key={`criterion-form-${i}`} crit={c} idx={i} onChange={updateCriterion} onRemove={removeCriterion} />
                  ))}
                  {form.criteria.length === 0 && <p className="text-white/20 text-xs text-center py-4">No criteria — add some above</p>}
                </div>
              </div>
              <div className="border-t border-white/5 pt-4">
                <KBSubtypesEditor
                  kbId={kb.id}
                  value={form.subtypes || []}
                  onChange={(next) => setForm(f => ({ ...f, subtypes: next }))}
                />
              </div>
              <div className="flex gap-2 pt-2 items-center flex-wrap">
                <button onClick={saveEdit} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  data-testid={`save-kb-${kb.id}`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save Changes
                </button>
                {isDomainKB && (
                  <button
                    onClick={async () => { await saveEdit({ keepOpen: true }); await handlePreviewRegrade(); }}
                    disabled={saving || previewing}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-200 text-sm font-medium rounded-lg disabled:opacity-50"
                    data-testid={`save-preview-kb-${kb.id}`}
                    title="Save the current edits and immediately preview the score shift on 5 sample calls"
                  >
                    {(saving || previewing) ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    Save &amp; Preview
                  </button>
                )}
                <button onClick={() => setEditing(false)} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 text-sm rounded-lg">Cancel</button>
                {isDomainKB && (
                  <div className="ml-auto flex items-center gap-2 text-xs">
                    <label className="text-white/40">Regrade last</label>
                    <select
                      value={regradeDays}
                      onChange={e => setRegradeDays(Number(e.target.value))}
                      className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-white text-xs focus:outline-none focus:border-purple-500/50"
                      data-testid={`regrade-days-${kb.id}`}
                    >
                      {[1, 3, 7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
                    </select>
                    <button
                      onClick={handlePreviewRegrade}
                      disabled={previewing || regrading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-xs rounded-md disabled:opacity-50"
                      data-testid={`regrade-preview-${kb.id}`}
                      title="Grade 5 sample calls WITHOUT saving — see the score shift before committing"
                    >
                      {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                      {previewing ? 'Sampling…' : 'Preview (5 calls)'}
                    </button>
                    <button
                      onClick={handleRegrade}
                      disabled={regrading || previewing}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 text-purple-200 text-xs rounded-md disabled:opacity-50"
                      data-testid={`regrade-kb-${kb.id}`}
                      title="Re-run grading on all historical calls of this KB's call_type using the freshly-saved grading instructions"
                    >
                      {regrading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                      {regrading ? 'Regrading…' : 'Regrade with new instructions'}
                    </button>
                  </div>
                )}
              </div>
              {previewResult && Array.isArray(previewResult.samples) && previewResult.samples.length > 0 && (
                <div className="mt-3 rounded-lg bg-purple-500/5 border border-purple-500/20 px-4 py-3 space-y-2" data-testid={`regrade-preview-result-${kb.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-purple-200 text-xs font-semibold uppercase tracking-wider">Preview — {previewResult.samples.filter(s => s.ok).length} samples graded (not saved)</p>
                      <p className="text-white/50 text-xs mt-1">
                        Current: {previewResult.summary?.current?.analyzed}A / {previewResult.summary?.current?.escalated}E · avg {previewResult.summary?.current?.avg_score ?? '—'}
                        <span className="text-white/30 mx-2">→</span>
                        Projected: {previewResult.summary?.projected?.analyzed}A / {previewResult.summary?.projected?.escalated}E · avg {previewResult.summary?.projected?.avg_score ?? '—'}
                      </p>
                    </div>
                    <button onClick={() => setPreviewResult(null)} className="text-white/30 hover:text-white/60"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="space-y-1">
                    {previewResult.samples.map(s => {
                      if (!s.ok) {
                        return <p key={s.call_id} className="text-red-300 text-[11px]">Error on {s.call_id}: {s.error}</p>;
                      }
                      const delta = s.delta;
                      const deltaColor = delta > 0 ? 'text-emerald-300' : delta < 0 ? 'text-red-300' : 'text-white/40';
                      const statusChanged = s.current_status !== s.projected_status;
                      return (
                        <div key={s.call_id} className="grid grid-cols-6 gap-2 items-center text-[11px] text-white/70">
                          <span className="col-span-2 font-mono text-white/50 truncate" title={s.call_id}>{s.call_id.slice(0, 18)}…</span>
                          <span className="text-right">{s.current_score}</span>
                          <span className="text-center text-white/30">→</span>
                          <span className="text-left">{s.projected_score}</span>
                          <span className={`text-right font-medium ${deltaColor}`}>
                            {delta > 0 ? `+${delta}` : delta}
                            {statusChanged && <span className="ml-1 text-amber-300">*</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-white/25 text-[10px]">* status flipped between analyzed/escalated · click &quot;Regrade with new instructions&quot; above to apply to all calls in the {previewResult.days}-day window.</p>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-white/40 text-sm">{kb.description}</p>
              <div className="space-y-1.5">
                {(kb.criteria || []).map((c, i) => (
                  <div key={`criterion-view-${i}`} className="flex items-start gap-2 text-sm">
                    <span className="text-teal-400 mt-0.5 flex-shrink-0">•</span>
                    <span className="text-white/60">{c.description}</span>
                    <span className="text-white/20 text-xs ml-auto flex-shrink-0">×{c.weight || 1}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="border-t border-white/5 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-white/40 text-xs font-medium">Reference Documents ({kb.documents?.length || 0})</p>
              <label className="flex items-center gap-1.5 text-teal-400 text-xs cursor-pointer hover:text-teal-300">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}Upload
                <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            </div>
            {(kb.documents || []).length > 0 ? (
              <div className="space-y-1.5">
                {kb.documents.map((d, i) => {
                  const fname = d.filename || d;
                  return (
                    <div key={`doc-${i}`} className="flex items-center gap-2 text-xs bg-white/[0.03] rounded-lg px-3 py-1.5">
                      <FileText className="w-3 h-3 text-white/30 shrink-0" />
                      <span className="text-white/50 flex-1 truncate">{fname}</span>
                      {d.uploaded_at && <span className="text-white/20 shrink-0">{d.uploaded_at?.slice(0, 10)}</span>}
                      <button onClick={() => handleDownload(fname)} className="text-teal-400/60 hover:text-teal-300 transition-colors" title="Download" data-testid={`download-doc-${i}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      </button>
                      <button onClick={() => handleDelete(fname)} className="text-white/20 hover:text-red-400 transition-colors" title="Delete" data-testid={`delete-doc-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-white/20 text-xs">No documents uploaded yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
